export const COOKIE_POLICY_STORAGE_KEY = 'lw-cookie-policy-preferences';
export const COOKIE_API_STATUS_STORAGE_KEY = 'lw-cookie-should-send-api-req';

export const COOKIE_POLICY_MINIMUM_DATE = {
  hermes: 1626790416781,
  zeus: 1626790416781,
} as const;

export const defaultCookiePreferencesValue = {
  mandatory: true,
  functional: false,
  analytical: false,
} as const;

export const functionalOnlyCookiePreferencesValue = {
  mandatory: true,
  functional: true,
  analytical: false,
} as const;

export const analyticalOnlyCookiePreferencesValue = {
  mandatory: true,
  functional: false,
  analytical: true,
} as const;

export const allTrueCookiePreferencesValue = {
  mandatory: true,
  functional: true,
  analytical: true,
} as const;

export const essentialOnlyTrueCookiePreferencesValue = {
  mandatory: true,
  functional: false,
  analytical: false,
} as const;

export type PlatformOptions = 'hermes' | 'zeus';
export type CookieGroupOption = 'mandatory' | 'functional' | 'analytical';

export type CookieList = {
  hermes: Section[];
  zeus: Section[];
};

export type Section = {
  sectionTitle: string;
  section: string;
  table: TableRow[];
};

export type TableRow = {
  cookieName: string;
  type: string;
  description: string;
};

export type StorageMap = {
  hermes: Platform;
  zeus: Platform;
};

export type Platform = {
  mandatory: string[];
  functional: string[];
  analytical: string[];
};

export const storageMap = (
  _cookieList: CookieList = cookieList(),
): StorageMap => {
  const storageMap = {} as StorageMap;
  for (const platform in _cookieList) {
    if ({}.hasOwnProperty.call(_cookieList, platform)) {
      storageMap[platform] = {};

      for (const section in _cookieList[platform]) {
        if ({}.hasOwnProperty.call(_cookieList[platform], section)) {
          const cookies = _cookieList[platform][section].table.map(
            cookie => cookie.cookieName,
          );
          storageMap[platform][_cookieList[platform][section].section] =
            cookies;
        }
      }
    }
  }
  return storageMap;
};

export const cookieList = () => ({
  hermes: [
    {
      sectionTitle: polyglot.t(
        'cookie_policy.page_three.cookie_list.essential_cookies',
      ),
      section: 'mandatory',
      table: [
        {
          cookieName: 'WAM_AUTH',
          type: polyglot.t(
            'cookie_policy.page_three.cookie_list.user_session_maintenance',
          ),
          description: polyglot.t(
            'cookie_policy.page_three.cookie_list.user_session_maintenance_description',
          ),
        },
        {
          cookieName: 'wamToken',
          type: polyglot.t(
            'cookie_policy.page_three.cookie_list.user_session_maintenance',
          ),
          description: polyglot.t(
            'cookie_policy.page_three.cookie_list.user_session_maintenance_description',
          ),
        },
        {
          cookieName: 'lw-language',
          type: polyglot.t(
            'cookie_policy.page_three.cookie_list.user_language_cookie',
          ),
          description: polyglot.t(
            'cookie_policy.page_three.cookie_list.user_language_cookie_description',
          ),
        },
        {
          cookieName: 'sharedAccountUsers',
          type: polyglot.t(
            'cookie_policy.page_three.cookie_list.shared_account_users',
          ),
          description: polyglot.t(
            'cookie_policy.page_three.cookie_list.shared_account_users_description',
          ),
        },
        {
          cookieName: 'persistence',
          type: polyglot.t(
            'cookie_policy.page_three.cookie_list.login_persistence',
          ),
          description: polyglot.t(
            'cookie_policy.page_three.cookie_list.login_persistence_description',
          ),
        },
        {
          cookieName: 'shouldPersistLoginPageLanguage',
          type: polyglot.t(
            'cookie_policy.page_three.cookie_list.should_persist_login_page_language',
          ),
          description: polyglot.t(
            'cookie_policy.page_three.cookie_list.should_persist_login_page_language_description',
          ),
        },
        {
          cookieName: 'AWSELB',
          type: polyglot.t('cookie_policy.page_three.cookie_list.oneHippo_cms'),
          description: polyglot.t(
            'cookie_policy.page_three.cookie_list.oneHippo_cms_description',
          ),
        },
        {
          cookieName: 'Cloudfront-*',
          type: polyglot.t(
            'cookie_policy.page_three.cookie_list.cloudfront_config',
          ),
          description: polyglot.t(
            'cookie_policy.page_three.cookie_list.cloudfront_config_description',
          ),
        },
        {
          cookieName: '_help_center_session',
          type: polyglot.t(
            'cookie_policy.page_three.cookie_list.help_center_session',
          ),
          description: polyglot.t(
            'cookie_policy.page_three.cookie_list.help_center_session_description',
          ),
        },
        {
          cookieName: '_zendesk*',
          type: polyglot.t(
            'cookie_policy.page_three.cookie_list.help_center_session',
          ),
          description: polyglot.t(
            'cookie_policy.page_three.cookie_list.help_center_session_description',
          ),
        },
        {
          cookieName: '__paypal*',
          type: polyglot.t('cookie_policy.page_three.cookie_list.paypal'),
          description: polyglot.t(
            'cookie_policy.page_three.cookie_list.paypal_description',
          ),
        },
        {
          cookieName: 'tokenExpiryTime',
          type: polyglot.t(
            'cookie_policy.page_three.cookie_list.token_expiry_time',
          ),
          description: polyglot.t(
            'cookie_policy.page_three.cookie_list.token_expiry_time_description',
          ),
        },
        {
          cookieName: 'cinemasOrder',
          type: polyglot.t(
            'cookie_policy.page_three.cookie_list.cinemas_order',
          ),
          description: polyglot.t(
            'cookie_policy.page_three.cookie_list.cinemas_order_description',
          ),
        },
        {
          cookieName: 'giftCardsOrder',
          type: polyglot.t(
            'cookie_policy.page_three.cookie_list.gift_cards_order',
          ),
          description: polyglot.t(
            'cookie_policy.page_three.cookie_list.gift_cards_order_description',
          ),
        },
        {
          cookieName: 'giftCardsSelectedDiningCard',
          type: polyglot.t(
            'cookie_policy.page_three.cookie_list.gift_cards_selected_dining_card',
          ),
          description: polyglot.t(
            'cookie_policy.page_three.cookie_list.gift_cards_selected_dining_card_description',
          ),
        },
        {
          cookieName: 'giftCardsSelectedGiftCard',
          type: polyglot.t(
            'cookie_policy.page_three.cookie_list.gift_cards_selected_gift_card',
          ),
          description: polyglot.t(
            'cookie_policy.page_three.cookie_list.gift_cards_selected_gift_card_description',
          ),
        },
        {
          cookieName: 'mfaSetup',
          type: polyglot.t('cookie_policy.page_three.cookie_list.mfa_setup'),
          description: polyglot.t(
            'cookie_policy.page_three.cookie_list.mfa_setup_description',
          ),
        },
        {
          cookieName: 'msTeamsReturnUrl',
          type: polyglot.t(
            'cookie_policy.page_three.cookie_list.ms_teams_return_url',
          ),
          description: polyglot.t(
            'cookie_policy.page_three.cookie_list.ms_teams_return_url_description',
          ),
        },
        {
          cookieName: 'regionalApiEndpoints',
          type: polyglot.t(
            'cookie_policy.page_three.cookie_list.regional_api_endpoints',
          ),
          description: polyglot.t(
            'cookie_policy.page_three.cookie_list.regional_api_endpoints_description',
          ),
        },
        {
          cookieName: 'retailerRedirectPayload',
          type: polyglot.t(
            'cookie_policy.page_three.cookie_list.retailer_redirect_payload',
          ),
          description: polyglot.t(
            'cookie_policy.page_three.cookie_list.retailer_redirect_payload_description',
          ),
        },
        {
          cookieName: 'shoponline-boosted-cashback',
          type: polyglot.t(
            'cookie_policy.page_three.cookie_list.shoponline_boosted_cashback',
          ),
          description: polyglot.t(
            'cookie_policy.page_three.cookie_list.shoponline_boosted_cashback_description',
          ),
        },
        {
          cookieName: 'signupCompany',
          type: polyglot.t(
            'cookie_policy.page_three.cookie_list.signup_company',
          ),
          description: polyglot.t(
            'cookie_policy.page_three.cookie_list.signup_company_description',
          ),
        },
        {
          cookieName: 'signupResponse',
          type: polyglot.t(
            'cookie_policy.page_three.cookie_list.signup_response',
          ),
          description: polyglot.t(
            'cookie_policy.page_three.cookie_list.signup_response_description',
          ),
        },
        {
          cookieName: 'signupSession',
          type: polyglot.t(
            'cookie_policy.page_three.cookie_list.signup_session',
          ),
          description: polyglot.t(
            'cookie_policy.page_three.cookie_list.signup_session_description',
          ),
        },
        {
          cookieName: 'SSOToken',
          type: polyglot.t('cookie_policy.page_three.cookie_list.sso_token'),
          description: polyglot.t(
            'cookie_policy.page_three.cookie_list.sso_token_description',
          ),
        },
        {
          cookieName: 'wamT',
          type: polyglot.t('cookie_policy.page_three.cookie_list.wam_t'),
          description: polyglot.t(
            'cookie_policy.page_three.cookie_list.wam_t_description',
          ),
        },
        {
          cookieName: 'useGlobalContent',
          type: polyglot.t(
            'cookie_policy.page_three.cookie_list.use_global_content',
          ),
          description: polyglot.t(
            'cookie_policy.page_three.cookie_list.use_global_content_description',
          ),
        },
        {
          cookieName: 'usePerksGlobalContent',
          type: polyglot.t(
            'cookie_policy.page_three.cookie_list.use_perks_global_content',
          ),
          description: polyglot.t(
            'cookie_policy.page_three.cookie_list.use_perks_global_content_description',
          ),
        },
        {
          cookieName: 'useAssessmentsGlobalContent',
          type: polyglot.t(
            'cookie_policy.page_three.cookie_list.use_assessments_global_content',
          ),
          description: polyglot.t(
            'cookie_policy.page_three.cookie_list.use_assessments_global_content_description',
          ),
        },
        {
          cookieName: 'isCreatingLimitedAccount',
          type: polyglot.t(
            'cookie_policy.page_three.cookie_list.is_creating_limited_account',
          ),
          description: polyglot.t(
            'cookie_policy.page_three.cookie_list.is_creating_limited_account_description',
          ),
        },
        {
          cookieName: 'darkMode',
          type: polyglot.t(
            'cookie_policy.page_three.cookie_list.dark_mode_cookie',
          ),
          description: polyglot.t(
            'cookie_policy.page_three.cookie_list.dark_mode_cookie_description',
          ),
        },
        {
          cookieName: 'lw-cookie-policy-preferences',
          type: polyglot.t(
            'cookie_policy.page_three.cookie_list.lw_cookie_policy_preferences',
          ),
          description: polyglot.t(
            'cookie_policy.page_three.cookie_list.lw_cookie_policy_preferences_description',
          ),
        },
        {
          cookieName: 'lw-cookie-should-send-api-req',
          type: polyglot.t(
            'cookie_policy.page_three.cookie_list.lw_cookie_should_send_api_req',
          ),
          description: polyglot.t(
            'cookie_policy.page_three.cookie_list.lw_cookie_should_send_api_req_description',
          ),
        },
        {
          cookieName: 'USLRelayState',
          type: polyglot.t(
            'cookie_policy.page_three.cookie_list.usl_relay_state',
          ),
          description: polyglot.t(
            'cookie_policy.page_three.cookie_list.usl_relay_state_description',
          ),
        },
      ],
    },
    {
      sectionTitle: polyglot.t(
        'cookie_policy.page_three.cookie_list.functional_cookies',
      ),
      section: 'functional',
      table: [
        {
          cookieName: 'recentPerksSearch',
          type: polyglot.t(
            'cookie_policy.page_three.cookie_list.recent_perks_search',
          ),
          description: polyglot.t(
            'cookie_policy.page_three.cookie_list.recent_perks_search_description',
          ),
        },
        {
          cookieName: 'walkthroughExcludeList',
          type: polyglot.t('cookie_policy.page_three.cookie_list.exclude_list'),
          description: polyglot.t(
            'cookie_policy.page_three.cookie_list.exclude_list_description',
          ),
        },
        {
          cookieName: 'isNativeStoreBannerDismissed',
          type: polyglot.t(
            'cookie_policy.page_three.cookie_list.is_native_store_banner_dismissed',
          ),
          description: polyglot.t(
            'cookie_policy.page_three.cookie_list.is_native_store_banner_dismissed_description',
          ),
        },
        {
          cookieName: 'isProfileEditDisclaimerTooltipDismissed',
          type: polyglot.t(
            'cookie_policy.page_three.cookie_list.is_profile_edit_disclaimer_tooltip_dismissed',
          ),
          description: polyglot.t(
            'cookie_policy.page_three.cookie_list.is_profile_edit_disclaimer_tooltip_dismissed_description',
          ),
        },
        {
          cookieName: 'pbMessage',
          type: polyglot.t('cookie_policy.page_three.cookie_list.pb_message'),
          description: polyglot.t(
            'cookie_policy.page_three.cookie_list.pb_message_description',
          ),
        },
        {
          cookieName: 'hasDismissedRegressionPopUp',
          type: polyglot.t(
            'cookie_policy.page_three.cookie_list.has_dismissed_regression_popup',
          ),
          description: polyglot.t(
            'cookie_policy.page_three.cookie_list.has_dismissed_regression_popup_description',
          ),
        },
      ],
    },
    {
      sectionTitle: polyglot.t(
        'cookie_policy.page_three.cookie_list.analytical_cookies',
      ),
      section: 'analytical',
      table: [
        {
          cookieName: '_ga',
          type: polyglot.t(
            'cookie_policy.page_three.cookie_list.non_personal_tracking',
          ),
          description: polyglot.t(
            'cookie_policy.page_three.cookie_list.non_personal_tracking_description',
          ),
        },
        {
          cookieName: '_gat',
          type: polyglot.t(
            'cookie_policy.page_three.cookie_list.non_personal_tracking',
          ),
          description: polyglot.t(
            'cookie_policy.page_three.cookie_list.non_personal_tracking_description',
          ),
        },
        {
          cookieName: '_gid',
          type: polyglot.t(
            'cookie_policy.page_three.cookie_list.non_personal_tracking',
          ),
          description: polyglot.t(
            'cookie_policy.page_three.cookie_list.non_personal_tracking_description',
          ),
        },
        {
          cookieName: 'CONSENT',
          type: polyglot.t(
            'cookie_policy.page_three.cookie_list.user_session_identification_cookie',
          ),
          description: polyglot.t(
            'cookie_policy.page_three.cookie_list.google_fonts_analytics',
          ),
        },
        {
          cookieName: 'DV',
          type: polyglot.t(
            'cookie_policy.page_three.cookie_list.non_personal_tracking',
          ),
          description: polyglot.t(
            'cookie_policy.page_three.cookie_list.google_embed_analytics',
          ),
        },
        {
          cookieName: '1P_JAR',
          type: polyglot.t(
            'cookie_policy.page_three.cookie_list.google_cookie',
          ),
          description: polyglot.t(
            'cookie_policy.page_three.cookie_list.google_embed_analytics',
          ),
        },
        {
          cookieName: 'giftCards*',
          type: polyglot.t(
            'cookie_policy.page_three.cookie_list.gift_card_oder',
          ),
          description: polyglot.t(
            'cookie_policy.page_three.cookie_list.gift_card_oder_description',
          ),
        },
      ],
    },
  ],
  zeus: [
    {
      sectionTitle: polyglot.t(
        'cookie_policy.page_three.cookie_list.essential_cookies',
      ),
      section: 'mandatory',
      table: [
        {
          cookieName: 'lw-language',
          type: polyglot.t(
            'cookie_policy.page_three.cookie_list.user_language_cookie',
          ),
          description: polyglot.t(
            'cookie_policy.page_three.cookie_list.user_language_cookie_desc',
          ),
        },
        {
          cookieName: 'wa-token',
          type: polyglot.t(
            'cookie_policy.page_three.cookie_list.user_session_identification_cookie',
          ),
          description: polyglot.t(
            'cookie_policy.page_three.cookie_list.user_session_identification_cookie_desc',
          ),
        },
        {
          cookieName: 'wa-persistence',
          type: polyglot.t(
            'cookie_policy.page_three.cookie_list.login_persistence',
          ),
          description: polyglot.t(
            'cookie_policy.page_three.cookie_list.login_persistence_desc',
          ),
        },
        {
          cookieName: 'wa-regionalEndpoints',
          type: polyglot.t(
            'cookie_policy.page_three.cookie_list.regional_endpoints_map',
          ),
          description: polyglot.t(
            'cookie_policy.page_three.cookie_list.regional_endpoints_map_desc',
          ),
        },
        {
          cookieName: 'wa-externalToken',
          type: polyglot.t(
            'cookie_policy.page_three.cookie_list.user_session_token',
          ),
          description: polyglot.t(
            'cookie_policy.page_three.cookie_list.user_session_token_desc',
          ),
        },
        {
          cookieName: 'wa-isGhostAdmin',
          type: polyglot.t(
            'cookie_policy.page_three.cookie_list.user_session_token',
          ),
          description: polyglot.t(
            'cookie_policy.page_three.cookie_list.user_session_token_desc',
          ),
        },
        {
          cookieName: 'USLRelayState',
          type: polyglot.t(
            'cookie_policy.page_three.cookie_list.usl_relay_state',
          ),
          description: polyglot.t(
            'cookie_policy.page_three.cookie_list.usl_relay_state_description',
          ),
        },
      ],
    },
    {
      sectionTitle: polyglot.t(
        'cookie_policy.page_three.cookie_list.functional_cookies',
      ),
      section: 'functional',
      table: [
        {
          cookieName: 'walkthroughExcludeList',
          type: polyglot.t(
            'cookie_policy.page_three.cookie_list.user_preference',
          ),
          description: polyglot.t(
            'cookie_policy.page_three.cookie_list.walkthrough_exclude_list',
          ),
        },
        {
          cookieName: 'wa-shouldPersistLoginPageLanguage',
          type: polyglot.t(
            'cookie_policy.page_three.cookie_list.user_preference',
          ),
          description: polyglot.t(
            'cookie_policy.page_three.cookie_list.should_persist_login_language',
          ),
        },
        {
          cookieName: 'wa-tieringWalkthroughHideList',
          type: polyglot.t(
            'cookie_policy.page_three.cookie_list.user_action_cookie_persistence',
          ),
          description: polyglot.t(
            'cookie_policy.page_three.cookie_list.keep_track_walkthrough',
          ),
        },
        {
          cookieName: 'rewardsVisited',
          type: polyglot.t(
            'cookie_policy.page_three.cookie_list.user_action_cookie_persistence',
          ),
          description: polyglot.t(
            'cookie_policy.page_three.cookie_list.keep_track_rewards_visited',
          ),
        },
        {
          cookieName: 'wa-defaultGroupBannerDismissed',
          type: polyglot.t(
            'cookie_policy.page_three.cookie_list.user_action_cookie_persistence',
          ),
          description: polyglot.t(
            'cookie_policy.page_three.cookie_list.keep_track_group_banner',
          ),
        },
        {
          cookieName: 'wa-gotIt',
          type: polyglot.t(
            'cookie_policy.page_three.cookie_list.user_choice_persistence_cookie',
          ),
          description: polyglot.t(
            'cookie_policy.page_three.cookie_list.keep_track_got_it',
          ),
        },
        {
          cookieName: 'assessmentPromotion',
          type: polyglot.t(
            'cookie_policy.page_three.cookie_list.user_choice_persistence_cookie',
          ),
          description: polyglot.t(
            'cookie_policy.page_three.cookie_list.user_choice_persistence_desc',
          ),
        },
        {
          cookieName: 'challenge-*',
          type: polyglot.t(
            'cookie_policy.page_three.cookie_list.user_progress_persistence_cookie',
          ),
          description: polyglot.t(
            'cookie_policy.page_three.cookie_list.user_progress_persistence_desc',
          ),
        },
        {
          cookieName: 'assessment-*',
          type: polyglot.t(
            'cookie_policy.page_three.cookie_list.user_progress_persistence_cookie',
          ),
          description: polyglot.t(
            'cookie_policy.page_three.cookie_list.user_progress_persistence_desc',
          ),
        },
        {
          cookieName: 'tierActivity-*',
          type: polyglot.t(
            'cookie_policy.page_three.cookie_list.user_progress_persistence_cookie',
          ),
          description: polyglot.t(
            'cookie_policy.page_three.cookie_list.user_progress_persistence_desc',
          ),
        },
      ],
    },
    {
      sectionTitle: polyglot.t(
        'cookie_policy.page_three.cookie_list.analytical_cookies',
      ),
      section: 'analytical',
      table: [
        {
          cookieName: '_ga',
          type: polyglot.t(
            'cookie_policy.page_three.cookie_list.non_personal_tracking',
          ),
          description: polyglot.t(
            'cookie_policy.page_three.cookie_list.non_personal_tracking_desc',
          ),
        },
      ],
    },
  ],
});
